import { Info, PencilLine } from 'lucide-react'
import { useId, useRef, useState, useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ConfirmModal } from './ConfirmModal'
import { Modal } from './Modal'
import { dismissCurrent, getModalQueue, subscribeModals } from './store'
import type { ModalRequest } from './types'

export function ModalHost() {
  const current = useSyncExternalStore(subscribeModals, getCurrent, getCurrent)
  if (!current) return null
  return <ModalSurface key={`${current.kind}:${current.title}:${getModalQueue().length}`} request={current} />
}

function getCurrent(): ModalRequest | null {
  return getModalQueue()[0] ?? null
}

function ModalSurface({ request }: { request: ModalRequest }) {
  const { t } = useTranslation()
  const [value, setValue] = useState(request.kind === 'prompt' ? (request.defaultValue ?? '') : '')
  const inputId = useId()
  const settled = useRef(false)

  function finish(result?: boolean | string | null) {
    if (settled.current) return
    settled.current = true
    dismissCurrent(result)
  }

  if (request.kind === 'confirm') {
    return (
      <ConfirmModal
        open
        onOpenChange={(next) => !next && finish(false)}
        title={request.title}
        description={request.description}
        confirmLabel={request.confirmLabel}
        cancelLabel={request.cancelLabel}
        destructive={request.destructive}
        onConfirm={() => finish(true)}
      />
    )
  }

  if (request.kind === 'alert') {
    return (
      <Modal
        open
        size="xs"
        variant="alert"
        icon={<Info />}
        onOpenChange={(next) => !next && finish()}
        title={request.title}
        description={request.description}
        footer={
          <Button type="button" onClick={() => finish()}>
            {request.confirmLabel ?? t('common.ok')}
          </Button>
        }
      />
    )
  }

  return (
    <Modal
      open
      size="sm"
      onOpenChange={(next) => !next && finish(null)}
      icon={<PencilLine />}
      title={request.title}
      description={request.description}
      onSubmit={(e) => {
        e.preventDefault()
        finish(value)
      }}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={() => finish(null)}>
            {request.cancelLabel ?? t('common.cancel')}
          </Button>
          <Button type="submit">{request.confirmLabel ?? t('common.save')}</Button>
        </>
      }
    >
      <Label htmlFor={inputId} className="sr-only">
        {request.title}
      </Label>
      <Input id={inputId} type={request.inputType ?? 'text'} value={value} placeholder={request.placeholder} autoFocus onChange={(e) => setValue(e.target.value)} />
    </Modal>
  )
}
